import { PlusIcon, TrashIcon } from '@heroicons/react/24/outline'
import { LayoutLoading } from 'components/LayoutLoading'
import { InputType } from 'config'
import { useEffect, useState } from 'react'
import { toast } from 'react-toastify'
import { getAdminConfig, getLoanChargeCategories, setAdminConfig } from 'services'
import { Button, Toggle } from 'stories/components'
import { confirm, InputConvert } from 'utils'
import { RenderInput } from 'utils/RenderInput'

/** Everything this screen owns: the fees, and what a borrower may do for themselves. */
export interface PayoffDefaultsConfig {
  fees: PayoffDefaultFee[]
  borrowerEstimate: boolean
  borrowerRequest: boolean
  /** Addresses copied on every payoff demand emailed from a loan, comma separated. */
  emailCc: string
  /** A line of the servicer's own wording added to the body of every payoff demand email. */
  emailNote: string
  /** Whether a quote asserts the default-rate spread, or offers it for the servicer to include. */
  includeDefaultInterest: boolean
}

/** One fee quoted on every payoff demand by default. */
export interface PayoffDefaultFee {
  title: string
  amount: string
  enabled: boolean
  /** Book this fee as a loan charge when a demand carrying it is approved. */
  postOnApproval?: boolean
  /** Charge category the booked fee files under. */
  chargeCategory?: string
}

const titleInput = (value: string): InputType => ({
  inputType: 'text',
  title: 'Fee Description',
  value,
})

const amountInput = (value: string): InputType => ({
  inputType: 'text',
  type: 'thousandSep',
  prefix: '$',
  title: 'Default Amount',
  value,
})

const emailCcInput = (value: string): InputType => ({
  inputType: 'text',
  title: 'Copy these addresses on every payoff email',
  placeholder: 'payoffs@yourcompany.com, servicing@yourcompany.com',
  value,
})

const emailNoteInput = (value: string): InputType => ({
  inputType: 'text',
  title: 'Line added to the payoff email',
  placeholder: 'Please reply all for best response',
  value,
})

/** Only meaningful for a fee that books on approval, so it greys out until that is switched on. */
const categoryInput = (value: string, categories: string[], disabled: boolean): InputType => ({
  inputType: 'select',
  title: 'Charge Category',
  hasDefaultOption: true,
  options: categories,
  value,
  disabled,
})

/**
 * The processing fees a servicer adds to every payoff quote - demand, reconveyance, release of
 * lien, and whatever else this lender charges.
 *
 * A priced fee lands on the payoff as its own line, already filled in and still editable on the
 * statement before it goes out. A fee left unpriced (or switched off) is not quoted, but stays in
 * the Payoff screen's "add category" picker so it can be added by hand when it applies. That is
 * why an unpriced fee is worth listing at all.
 *
 * "Charge on approval" books the fee onto the loan as a real charge the moment a demand carrying it
 * is approved - quoting a fee and collecting it are different acts, so nothing is booked while the
 * statement is still a draft or pending.
 */
export const PayoffDefaultsConfiguration = () => {
  const [isLoading, setLoading] = useState(true)
  const [fees, setFees] = useState<PayoffDefaultFee[]>([])
  const [categories, setCategories] = useState<string[]>([])
  const [borrowerEstimate, setBorrowerEstimate] = useState(false)
  const [borrowerRequest, setBorrowerRequest] = useState(false)
  // Defaults on, so a tenant that saved this screen before the switch existed keeps quoting
  // default interest rather than silently dropping it from every demand.
  const [includeDefaultInterest, setIncludeDefaultInterest] = useState(true)
  const [emailCc, setEmailCc] = useState('')
  const [emailNote, setEmailNote] = useState('')

  useEffect(() => {
    fetchFees()
  }, [])

  const fetchFees = async () => {
    setLoading(true)
    try {
      const [data, cats] = await Promise.all([getAdminConfig('payoffDefaults'), getLoanChargeCategories(true)])
      // The setting shipped as a bare array of fees before it grew the borrower switches.
      setFees(Array.isArray(data) ? data : data?.fees || [])
      setBorrowerEstimate(data?.borrowerEstimate === true)
      setBorrowerRequest(data?.borrowerRequest === true)
      setIncludeDefaultInterest(data?.includeDefaultInterest !== false)
      setEmailCc(`${data?.emailCc || ''}`)
      setEmailNote(`${data?.emailNote || ''}`)
      setCategories((Array.isArray(cats) ? cats : []).map((c: any) => `${c?.value ?? ''}`).filter(Boolean))
    } catch (error) {
      console.error('Error fetching payoff defaults:', error)
      setFees([])
    } finally {
      setLoading(false)
    }
  }

  const onChange = (index: number, key: keyof PayoffDefaultFee, value: any) => {
    setFees((prev) => prev.map((fee, i) => (i === index ? { ...fee, [key]: value } : fee)))
  }

  const onAdd = () => {
    setFees((prev) => [...prev, { title: '', amount: '', enabled: true, postOnApproval: false, chargeCategory: '' }])
  }

  const onRemove = async (index: number) => {
    const fee = fees[index]
    const result = await confirm(`Remove "${fee.title || 'this fee'}" from the payoff defaults?`)
    if (!result) return
    setFees((prev) => prev.filter((_, i) => i !== index))
  }

  const onSave = async () => {
    const cleaned = fees.map((fee) => ({ ...fee, title: (fee.title || '').trim() }))
    if (cleaned.some((fee) => !fee.title)) {
      toast('Every payoff fee needs a description.', { type: 'error' })
      return
    }
    setLoading(true)
    try {
      await setAdminConfig('payoffDefaults', {
        fees: cleaned,
        borrowerEstimate,
        borrowerRequest,
        includeDefaultInterest,
        emailCc: emailCc.trim(),
        emailNote: emailNote.trim(),
      })
      setFees(cleaned)
      toast('Payoff defaults saved.', { type: 'success' })
    } catch (error) {
      console.error('Error saving payoff defaults:', error)
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="relative">
      <LayoutLoading show={isLoading} />
      <div className="mb-4">
        <h2 className="text-lg font-semibold text-gray-800">Payoff Defaults</h2>
        <p className="text-sm text-gray-600 mt-1">
          Fees added to every payoff demand. A fee with an amount is quoted automatically and stays editable on the
          statement; a fee left blank or switched off is offered in the Payoff screen&apos;s category picker instead.
        </p>
        <p className="text-sm text-gray-600 mt-1">
          <span className="font-medium">Charge on approval</span> books the fee onto the loan as a charge once a demand
          carrying it is approved. Nothing is booked from a draft or an unapproved statement, and a fee already sitting
          unpaid on the loan is not billed twice.
        </p>
      </div>

      {/* Off does not hide default interest or stop it being worked out: the line still reaches the
          quote with its figure, the days it covers and the rate, sitting toggled off so it is one
          click to charge. That suits a servicer who decides per demand whether a delinquent
          borrower is actually being charged the default rate, rather than deleting the line from
          every quote. */}
      <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 mb-6 max-w-3xl">
        <div className="font-medium text-gray-800 mb-1">Default Interest</div>
        <p className="text-sm text-gray-600 mb-3">
          Whether a payoff or reinstatement quote charges the default-rate spread on a loan past its default trigger,
          or offers it. Turned off, the line is still calculated and shown on the quote, switched off and out of the
          total until someone includes it.
        </p>
        <Toggle
          id="payoff-include-default-interest"
          title="Charge default interest on a quote without being asked"
          value={includeDefaultInterest}
          onChange={(value: boolean) => setIncludeDefaultInterest(value)}
        />
      </div>

      {/* The two borrower switches are independent: publishing a figure a borrower can pull at will
          and accepting a request to issue one are different decisions, and a lender may well want
          the second without the first. Both start off. */}
      <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 mb-6 max-w-3xl">
        <div className="font-medium text-gray-800 mb-1">Borrower Portal</div>
        <p className="text-sm text-gray-600 mb-3">
          What a borrower may do for themselves. Anything they download is always marked DRAFT: it books no fees and
          cannot be approved, so it can never be mistaken for a demand a title company can wire against.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          <Toggle
            id="payoff-borrower-estimate"
            title="Borrowers can download their own payoff estimate"
            value={borrowerEstimate}
            onChange={(value: boolean) => setBorrowerEstimate(value)}
          />
          <Toggle
            id="payoff-borrower-request"
            title="Borrowers can request an official payoff statement"
            value={borrowerRequest}
            onChange={(value: boolean) => setBorrowerRequest(value)}
          />
        </div>
      </div>

      {/* A demand is quoted to somebody outside the company, so the desk that issued it needs a
          copy of what went out, and whoever receives it needs telling how to reply so the answer
          reaches a team rather than one person's inbox. Both are this lender's wording and this
          lender's mailbox, so both are settings. */}
      <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 mb-6 max-w-3xl">
        <div className="font-medium text-gray-800 mb-1">Payoff Email</div>
        <p className="text-sm text-gray-600 mb-3">
          Applies to every payoff statement and partial release demand emailed from the Payoff screen. Copied addresses
          receive the same attachment as the recipient; anyone already on the To line is not copied twice. Leave either
          blank to send what you send today.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <RenderInput
            input={emailCcInput(emailCc)}
            Key="payoff-email-cc"
            onChange={(_: string, value: any) => setEmailCc(value)}
          />
          <RenderInput
            input={emailNoteInput(emailNote)}
            Key="payoff-email-note"
            onChange={(_: string, value: any) => setEmailNote(value)}
          />
        </div>
      </div>

      <div className="font-medium text-gray-800 mb-2">Payoff Fees</div>

      {fees.length === 0 && <div className="text-sm text-gray-500 italic mb-4">No payoff fees configured yet.</div>}

      <div className="space-y-2 mb-4">
        {fees.map((fee, index) => (
          <div className="flex items-center gap-3" key={index}>
            <div className="flex-1 grid grid-cols-3 gap-3">
              <RenderInput
                input={titleInput(fee.title)}
                Key={`${index}_title`}
                onChange={(_: string, value: any) => onChange(index, 'title', value)}
              />
              <RenderInput
                input={amountInput(fee.amount)}
                Key={`${index}_amount`}
                onChange={(_: string, value: any) =>
                  onChange(index, 'amount', InputConvert(amountInput(fee.amount), value))
                }
              />
              <RenderInput
                input={categoryInput(fee.chargeCategory || '', categories, !fee.postOnApproval)}
                Key={`${index}_chargeCategory`}
                onChange={(_: string, value: any) => onChange(index, 'chargeCategory', value)}
              />
            </div>
            <div className="w-28 shrink-0">
              <Toggle
                id={`payoff-fee-enabled-${index}`}
                title="Quote by default"
                value={fee.enabled}
                onChange={(value: boolean) => onChange(index, 'enabled', value)}
              />
            </div>
            <div className="w-32 shrink-0">
              <Toggle
                id={`payoff-fee-charge-${index}`}
                title="Charge on approval"
                value={fee.postOnApproval === true}
                onChange={(value: boolean) => onChange(index, 'postOnApproval', value)}
              />
            </div>
            <span
              className="w-6 shrink-0 flex justify-center text-red-500 hover:cursor-pointer hover-shadow1 rounded"
              onClick={() => onRemove(index)}
            >
              <TrashIcon className="w-4 h-4" />
            </span>
          </div>
        ))}
      </div>

      <div className="flex items-center gap-3">
        <Button link onClick={onAdd} color="shade-blue">
          <span className="flex items-center gap-1">
            <PlusIcon className="w-4 h-4" />
            Add Fee
          </span>
        </Button>
        <Button onClick={onSave}>Save</Button>
      </div>
    </div>
  )
}
